import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Shared slide-deck navigation for /pitch, /how-it-works, and /partners:
 * arrow-key navigation, swipe left/right on the stage, a scroll reset so
 * each slide opens at the top when it's taller than the viewport, and
 * 1-based hash deep-linking (/pitch#5 opens slide 5).
 */
export default function useDeckNavigation(total) {
  const [current, setCurrent] = useState(() => {
    const n = parseInt(window.location.hash.slice(1), 10);
    return Number.isNaN(n) ? 0 : Math.min(Math.max(n - 1, 0), total - 1);
  });
  const stageRef = useRef(null);
  const touchStart = useRef(null);

  const prev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);
  const next = useCallback(() => setCurrent((c) => Math.min(total - 1, c + 1)), [total]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  useEffect(() => {
    stageRef.current?.scrollTo(0, 0);
  }, [current]);

  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    // Horizontal swipe changes slides; vertical movement is scrolling.
    if (Math.abs(dx) > 60 && Math.abs(dx) > 1.5 * Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
    }
  };

  return {
    current,
    prev,
    next,
    stageProps: { ref: stageRef, onTouchStart, onTouchEnd },
  };
}
